import { useEffect, useRef, useState } from 'react'
import type { CSSProperties, PointerEvent as ReactPointerEvent, ReactNode } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { BUSINESS } from '@shared/config'
import { useSeo } from '@shared/lib/seo'
import { getAllPosts, getAllCategories } from '@shared/lib/posts'
import { useAppStore } from '@shared/store'
import { Header } from '@widgets/header'
import { Footer } from '@widgets/footer'
import { INITIAL_POPUP_SETTINGS } from '../model/initial-popups'
import * as styles from './HomePage.css'

const MEDIA = {
  visualVideo: '/images/samples/hero.webm',
} as const

const HERO_SLIDES = [
  { kind: 'video' as const, source: MEDIA.visualVideo, poster: '/images/samples/hero-poster.webp' },
]

const SOLUTIONS = [
  { eyebrow: '01', title: '돈코츠라멘', description: '직화구이 챠슈와 반숙계란,\n12시간 이상 끓여낸 진한 돼지뼈 국물이 일품입니다.', photo: '/images/menu/돈코츠라멘.webp', tone: ['#111111', '#404040'] },
  { eyebrow: '02', title: '마제면', description: '직화로 구운 돼지목살과 비법 중화 볶음고명,\n어분이 어우러진 나고야풍 일본식 비빔면입니다.', photo: '/images/menu/마제면.webp', tone: ['#181818', '#5b5b5b'] },
  { eyebrow: '03', title: '탄탄멘(매운맛)', description: '특별한 양념으로 볶은 돼지고기와 반숙계란,\n진하고 얼큰한 국물의 일본라멘입니다.', photo: '/images/menu/탄탄멘.webp', tone: ['#202020', '#707070'] },
  { eyebrow: '04', title: '부타동(돼지고기덮밥)', description: '특제소스로 맛을 낸 돼지 목살을 직화로 구워\n불향이 살아있는 일본식 돼지고기 덮밥입니다.', photo: '/images/menu/부타동(돼지고기덮밥).webp', tone: ['#292929', '#666666'] },
  { eyebrow: '05', title: '명란소고기덮밥', description: '고소하게 구운 소고기와\n특제 명란소스가 조화로운 덮밥입니다.', photo: '/images/menu/명란소고기덮밥.webp', tone: ['#151515', '#4a4a4a'] },
  { eyebrow: '06', title: '미도리라멘', description: '향긋한 바질과\n진한 돈골육수가 만난 라멘입니다.', photo: '/images/menu/미도리라멘.webp', tone: ['#242424', '#858585'] },
  { eyebrow: '07', title: '카라멘', description: '얼큰하고 깔끔한 닭육수에\n다양하고 풍부한 토핑을 더한 해장라멘입니다.', photo: '/images/menu/카라멘.webp', tone: ['#1b1b1b', '#505050'] },
  { eyebrow: '08', title: '치킨가라아게(5pc)', description: '닭다리살만을 엄선해 특별한 레시피로 튀겨낸\n쫄깃하고 고소한 사이드 메뉴입니다.', photo: '/images/menu/치킨가라아게(5pc).webp', tone: ['#303030', '#777777'] },
]

const RENTALS = [
  {
    title: '안양 최장수 정통 일본 라멘 전문점',
    description: '우마이식당은 안양 최장수 일본 라멘 전문점으로\n성공과 성장을 일궈냈습니다.',
    price: '가맹 사업 안내',
        image: '/images/samples/최장수.webp',
    mobileImage: '/images/samples/최장수.webp',
  },
  {
    title: '점주님의 평생 동반자',
    description: '성공과 성장을 이끈 저력과 섬세함으로\n함께하는 가맹점 점주님의 동반자가 되겠습니다.',
    price: '운영 기준 확인',
    image: '/images/samples/신뢰.webp',
    mobileImage: '/images/samples/신뢰.webp',
  },
  {
    title: '상생으로 이어지는 다음 장면',
    description: '우마이식당의 모토는 상생입니다.\n모든 궁금증을 해결해드리고 창업 상담에 답하겠습니다.',
    price: '가맹 상담 신청',
    image: '/images/samples/상생.webp',
    mobileImage: '/images/samples/상생.webp',
  },
]

const REASONS = [
  { image: '/images/samples/빠른조리.webp', title: '전 메뉴 빠른 조리', description: '빠른 조리로 고객의 기다림을 줄이고\n매장의 회전율을 높입니다.' },
  { image: '/images/samples/위생적인 물류.webp', title: '위생적인 물류 생산 및 공급', description: '안정적이고 위생적인 생산과 공급으로\n매장 운영의 기본을 지킵니다.' },
  { image: '/images/samples/점주님과함께하는 상생.webp', title: '점주님과 함께하는 상생', description: '본사와 가맹점이 함께 성장하는\n우마이식당의 가장 중요한 원칙입니다.' },
  { image: '/images/samples/소자본창업비용.webp', title: '소자본 창업 비용', description: '점주님의 부담은 낮추고 본사 마진은 줄여\n점주님의 마진율을 높입니다.' },
]

const BUSINESSES = [
  { eyebrow: '01', title: '성공과 성장을 만든 경험', description: '안양 최장수 일본 라멘 전문점으로\n우마이식당의 성공과 성장을 일궈냈습니다.', tone: ['#111111', '#4c4c4c'], action: '브랜드 소개' },
  { eyebrow: '02', title: '평생의 동반자가 되겠습니다', description: '성공과 성장을 이끈 저력과 섬세함으로\n함께하는 점주님의 평생을 동행합니다.', tone: ['#1c1c1c', '#6a6a6a'], action: '가맹 안내' },
  { eyebrow: '03', title: '모든 궁금증을 해결합니다', description: '우마이식당의 모토는 상생입니다.\n창업 상담 문의를 남겨주시면 바로 연락드리겠습니다.', tone: ['#242424', '#8a8a8a'], action: '창업 상담 문의' },
]

const METRICS = [
  { title: 'ROOT', value: 10, unit: '년+', description: '범계에서 시작한 우마이식당', detail: '안양 최장수 정통 일본 라멘 전문점', tone: ['#111111', '#4d4d4d'] },
  { title: 'MENU', value: 8, unit: '가지', description: '라멘·돈부리·사이드 메뉴', detail: '한국인의 입맛에 맞춘 우마이식당의 라인업', tone: ['#181818', '#5f5f5f'] },
  { title: 'BROTH', value: 12, unit: '시간+', description: '돈코츠라멘의 진한 돼지뼈 국물', detail: '12시간 이상 정성껏 끓여냅니다', tone: ['#e6e6e6', '#ffffff'] },
]

function classNames(...values: Array<string | false | undefined>) {
  return values.filter(Boolean).join(' ')
}

function originRootSize(width: number) {
  if (width <= 390) return width * 0.021795
  if (width <= 412) return width * 0.024272
  if (width <= 480) return width * 0.022
  if (width <= 640) return width * 0.017188
  if (width <= 800) return width * 0.01375
  if (width <= 1280) return width * 0.008594
  if (width <= 1536) return width * 0.00651
  return 10
}

function setFillPosition(event: ReactPointerEvent<HTMLElement>) {
  const rect = event.currentTarget.getBoundingClientRect()
  event.currentTarget.style.setProperty('--fill-x', `${event.clientX - rect.left}px`)
  event.currentTarget.style.setProperty('--fill-y', `${event.clientY - rect.top}px`)
}

function Reveal({ children, className, style, onPointerMove, onClick, rootMargin }: { children: ReactNode; className?: string; style?: CSSProperties; onPointerMove?: (event: ReactPointerEvent<HTMLDivElement>) => void; onClick?: () => void; rootMargin?: string }) {
  const [visible, setVisible] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const node = ref.current
    if (!node) return
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setVisible(true)
        observer.disconnect()
      }
    }, { threshold: 0.12, rootMargin })
    observer.observe(node)
    return () => observer.disconnect()
  }, [])

  return <div ref={ref} style={style} onPointerMove={onPointerMove} onClick={onClick} className={classNames(styles.reveal, visible && styles.revealVisible, className)}>{children}</div>
}

function InitialPopup() {
  const [dismissed, setDismissed] = useState<string[]>([])
  const popups = INITIAL_POPUP_SETTINGS.items.filter((item) => item.enabled)
  const now = new Date()
  const todayKey = `${INITIAL_POPUP_SETTINGS.storageKey}-${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`

  useEffect(() => {
    try {
      const stored = window.localStorage.getItem(todayKey)
      if (stored) setDismissed(JSON.parse(stored) as string[])
    } catch {
      // Private browsing and storage-disabled environments should still show the popups.
    }
  }, [todayKey])

  const visible = popups.filter((popup) => !dismissed.includes(popup.id))

  useEffect(() => {
    if (visible.length === 0) return
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = previousOverflow }
  }, [visible.length])

  if (visible.length === 0) return null

  const dismiss = (popupId: string, rememberForToday = false) => {
    setDismissed((current) => {
      const next = current.includes(popupId) ? current : [...current, popupId]
      if (rememberForToday) {
        try { window.localStorage.setItem(todayKey, JSON.stringify(next)) } catch { /* storage is optional */ }
      }
      return next
    })
  }

  return (
    <div className={styles.popupBackdrop} role="dialog" aria-modal="true" aria-label="안내 팝업">
      <button type="button" className={styles.popupCloseAll} onClick={() => setDismissed(popups.map((popup) => popup.id))}>전체 닫기 <span aria-hidden="true">×</span></button>
      <div className={styles.popupGrid}>
        {visible.map((popup) => (
          <figure key={popup.id} className={styles.popupCard}>
            <img src={popup.image} alt={popup.alt} />
            <figcaption className={styles.popupCardActions}>
              <button type="button" onClick={() => dismiss(popup.id, true)}>오늘 그만보기</button>
              <button type="button" onClick={() => dismiss(popup.id)}>창 닫기</button>
            </figcaption>
          </figure>
        ))}
      </div>
    </div>
  )
}

function HeroSection() {
  const [active, setActive] = useState(0)
  const [introFinished, setIntroFinished] = useState(false)
  // 영상 지연 로딩: 처음엔 첫 프레임 이미지(poster), 영상이 다 로딩되면 교체
  const [videoReady, setVideoReady] = useState(false)
  const videoRef = useRef<HTMLVideoElement>(null)

  useEffect(() => {
    const timer = window.setInterval(() => setActive((value) => (value + 1) % HERO_SLIDES.length), 6000)
    const introTimer = window.setTimeout(() => setIntroFinished(true), 1500)
    return () => {
      window.clearInterval(timer)
      window.clearTimeout(introTimer)
    }
  }, [])

  useEffect(() => {
    // 이미 캐시된 경우 onCanPlayThrough가 리스너 연결 전 발생할 수 있으므로 보정
    const v = videoRef.current
    if (v && v.readyState >= 4) setVideoReady(true)
  }, [])

  const move = (direction: -1 | 1) => setActive((value) => (value + direction + HERO_SLIDES.length) % HERO_SLIDES.length)

  return (
    <section id="top" className={styles.hero} aria-label="메인 비주얼">
      <div className={classNames(styles.heroFrame, introFinished && styles.heroFrameActive)}>
        {HERO_SLIDES.map((slide, index) => slide.kind === 'video' ? (
          <div key={slide.source}>
            {slide.poster && (
              <img
                className={classNames(styles.heroMedia, active === index && !videoReady && styles.heroMediaActive)}
                src={slide.poster}
                alt=""
                aria-hidden="true"
              />
            )}
            <video
              ref={videoRef}
              className={classNames(styles.heroMedia, active === index && videoReady && styles.heroMediaActive)}
              src={slide.source}
              poster={slide.poster}
              preload="auto"
              autoPlay
              muted
              loop
              playsInline
              aria-hidden="true"
              onCanPlayThrough={() => setVideoReady(true)}
            />
          </div>
        ) : (
          <img key={slide.source} className={classNames(styles.heroMedia, styles.heroImageMedia, active === index && styles.heroMediaActive, active === index && styles.heroImageMediaActive)} src={slide.source} alt="" aria-hidden="true" />
        ))}
        <span className={styles.heroShade} aria-hidden="true" />
        <span className={styles.heroCoverTop} aria-hidden="true"><span className={styles.heroCoverTopCornerA} /><span className={styles.heroCoverTopCornerB} /></span>
        <span className={styles.heroCoverBottom} aria-hidden="true"><span className={styles.heroCoverBottomCornerA} /><span className={styles.heroCoverBottomCornerB} /></span>
      </div>
      <div className={styles.heroCopy}>
        <p className={styles.heroKicker}>안양 최장수 정통 일본 라멘 전문점</p>
        <h1 className={styles.heroTitle}>정통 일본 라멘 본래의 맛을<br /><span className={styles.heroAccent}>한국의 맛으로 재해석</span></h1>
        <a className={styles.heroCta} href="#inquiry">가맹 상담 신청</a>
      </div>
      <div className={styles.heroControls}>
        {HERO_SLIDES.length > 1 && <>
          <button type="button" className={styles.heroArrow} aria-label="이전 비주얼" onClick={() => move(-1)}>‹</button>
          <button type="button" className={styles.heroArrow} aria-label="다음 비주얼" onClick={() => move(1)}>›</button>
          <div className={styles.heroProgress} aria-label={`현재 비주얼 ${active + 1} / ${HERO_SLIDES.length}`}>
            {HERO_SLIDES.map((slide, index) => <span key={slide.source} className={classNames(styles.heroDot, active === index && styles.heroDotActive)} />)}
          </div>
        </>}
      </div>
    </section>
  )
}

function SolutionsSection() {
  const [position, setPosition] = useState(0)
  const [isPointerInside, setIsPointerInside] = useState(false)
  const [isPanoramaStarted, setIsPanoramaStarted] = useState(false)
  const drag = useRef({ active: false, startX: 0, startPosition: 0 })
  const positionRef = useRef(0)
  const panoramaStageRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const stage = panoramaStageRef.current
    if (!stage) return

    let startTimer = 0
    const start = () => {
      startTimer = window.setTimeout(() => setIsPanoramaStarted(true), 1200)
    }

    if (typeof IntersectionObserver === 'undefined') {
      start()
      return () => window.clearTimeout(startTimer)
    }

    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return
      observer.disconnect()
      start()
    }, { threshold: 0 })

    observer.observe(stage)
    return () => {
      observer.disconnect()
      window.clearTimeout(startTimer)
    }
  }, [])

  useEffect(() => {
    let frame = 0
    let previous = performance.now()

    const tick = (now: number) => {
      const elapsed = Math.min(now - previous, 80)
      previous = now
      if (isPanoramaStarted && !drag.current.active && !isPointerInside && document.visibilityState === 'visible') {
        positionRef.current = (positionRef.current + elapsed / 4000 + SOLUTIONS.length) % SOLUTIONS.length
        setPosition(positionRef.current)
      }
      frame = requestAnimationFrame(tick)
    }

    frame = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(frame)
  }, [isPanoramaStarted, isPointerInside])

  const viewportWidth = typeof window === 'undefined' ? 1280 : window.innerWidth
  const rootSize = originRootSize(viewportWidth)
  const cardWidth = viewportWidth <= 800
    ? viewportWidth <= 390
      ? viewportWidth * 0.5885
      : viewportWidth <= 480
        ? viewportWidth * 0.594
        : 352
    : 36 * rootSize
  const preset = viewportWidth <= 480
    ? { depth: 180, rotate: 10, bend: 0.35, moveDiv: 4.2, gap: 10 }
    : viewportWidth <= 800
      ? { depth: 220, rotate: 14, bend: 0.45, moveDiv: 3.8, gap: 18 }
      : viewportWidth <= 1024
        ? { depth: 280, rotate: 18, bend: 0.65, moveDiv: 3.4, gap: 30 }
        : { depth: 320, rotate: 22, bend: 0.85, moveDiv: 3, gap: 40 }
  const centerGap = cardWidth + preset.gap
  const ratio = preset.rotate / 180
  const radians = (preset.rotate * Math.PI) / 180 / 2

  const relativePosition = (index: number) => {
    let distance = index - position
    while (distance > SOLUTIONS.length / 2) distance -= SOLUTIONS.length
    while (distance < -SOLUTIONS.length / 2) distance += SOLUTIONS.length
    return distance
  }

  const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    drag.current = { active: true, startX: event.clientX, startPosition: positionRef.current }
    event.currentTarget.setPointerCapture(event.pointerId)
  }

  const onPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!drag.current.active) return
    const distance = (event.clientX - drag.current.startX) / Math.max(centerGap, 1)
    positionRef.current = drag.current.startPosition - distance
    setPosition(positionRef.current)
  }

  const stopDrag = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (drag.current.active) {
      const distance = (event.clientX - drag.current.startX) / Math.max(centerGap, 1)
      positionRef.current = Math.round(drag.current.startPosition - distance)
      setPosition(positionRef.current)
    }
    if (drag.current.active && event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId)
    drag.current.active = false
  }

  const panoramaItems = SOLUTIONS.map((solution, index) => {
    const distance = relativePosition(index)
    if (Math.abs(distance) > 3.6) return null
    const progress = -distance
    const scaleFactor = 1 - Math.cos(progress * ratio * Math.PI)
    const x = progress * (cardWidth / preset.moveDiv) * scaleFactor
    const z = (cardWidth * 0.5) / Math.sin(radians) * scaleFactor - preset.depth
    const rotateY = progress * preset.rotate
    const bend = progress * preset.bend * 6
    return { solution, index, distance, x, z, rotateY, bend, visualCenter: distance * centerGap + x }
  }).filter((item): item is NonNullable<typeof item> => item !== null)
  const mainItem = panoramaItems.reduce((closest, item) => Math.abs(item.visualCenter) < Math.abs(closest.visualCenter) ? item : closest, panoramaItems[0])

  return (
    <section id="solutions" className={classNames(styles.section, styles.solutionSection)}>
      <Reveal className={styles.sectionHead}>
        <h2 className={styles.sectionTitle}>정통 일본 라멘 본래의 맛을<br /><span className={styles.sectionTitleAccent}>한국의 맛으로 재해석</span>합니다</h2>
        <p className={styles.sectionDescription}>한국인의 입맛에 맞는 정통 돈부리와<br />가볍게 즐길 수 있는 사이드 메뉴까지 함께합니다.</p>
      </Reveal>
      <div className={styles.panorama}>
        <span className={classNames(styles.dragBadge, !isPointerInside && styles.dragBadgeHidden)} aria-hidden="true">↔</span>
        <div
          className={styles.panoramaStage}
          ref={panoramaStageRef}
          onPointerEnter={() => setIsPointerInside(true)}
          onPointerLeave={() => setIsPointerInside(false)}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={stopDrag}
          onPointerCancel={stopDrag}
        >
          {panoramaItems.map(({ solution, index, distance, x, z, rotateY, bend }) => {
            const isMain = mainItem.index === index
            return (
              <article
                key={solution.title}
                className={classNames(styles.panoramaCard, isMain && styles.panoramaCardActive)}
                style={{
                  transform: `translate3d(calc(-50% + ${distance * centerGap + x}px), 0px, ${isMain ? z + 28 : z}px) rotateY(${isMain ? rotateY * .2 : rotateY}deg)`,
                  zIndex: isMain ? 30 : 10,
                }}
                onClick={() => {
                  positionRef.current = Math.round(positionRef.current) + Math.round(distance)
                  setPosition(positionRef.current)
                }}
              >
                <div
                  className={styles.panoramaCardInner}
                  style={{ transform: `translateZ(0) rotateY(${isMain ? bend * .12 : bend * .35}deg)` }}
                >
                  <div className={styles.panoramaCardImage} style={{ background: `linear-gradient(145deg, rgba(0,0,0,.18), rgba(0,0,0,.48)), url("${solution.photo}") center / cover` }} aria-hidden="true">
                    <span className={styles.panoramaCardArtworkMark}>{solution.eyebrow}</span>
                  </div>
                  <span className={styles.panoramaCardShade} aria-hidden="true" />
                  <div className={styles.panoramaCardCopy}>
                    <h3 className={styles.panoramaCardTitle}>{solution.title}</h3>
                    <p className={styles.panoramaCardDescription}>{solution.description}</p>
                  </div>
                </div>
              </article>
            )
          })}
        </div>
      </div>
    </section>
  )
}

function RentalSection() {
  const [active, setActive] = useState(0)
  const [dragOffset, setDragOffset] = useState(0)
  const [isDragging, setIsDragging] = useState(false)
  const stageRef = useRef<HTMLDivElement>(null)
  const drag = useRef({ active: false, startX: 0, startY: 0, pointerId: 0, axis: null as 'x' | 'y' | null })

  const move = (direction: -1 | 1) => {
    setDragOffset(0)
    setActive((value) => Math.max(0, Math.min(RENTALS.length - 1, value + direction)))
  }

  const finishDrag = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!drag.current.active) return
    const delta = event.clientX - drag.current.startX
    const threshold = Math.max(42, Math.min(96, event.currentTarget.clientWidth * .08))
    if (drag.current.axis === 'x' && Math.abs(delta) > threshold) move(delta < 0 ? 1 : -1)
    drag.current.active = false
    drag.current.axis = null
    setIsDragging(false)
    setDragOffset(0)
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId)
  }

  const cancelDrag = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!drag.current.active) return
    drag.current.active = false
    drag.current.axis = null
    setIsDragging(false)
    setDragOffset(0)
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId)
  }

  const relativeIndex = (index: number) => index - active

  return (
    <section id="rental" className={classNames(styles.section, styles.rentalSection)}>
      <Reveal className={styles.sectionHead}>
        <h2 className={styles.sectionTitle}>우마이식당의 성공과 성장을<br /><span className={styles.sectionTitleAccent}>함께 이어갑니다</span></h2>
        <p className={styles.sectionDescription}>안양 최장수 정통 일본 라멘 전문점 우마이식당의<br />경험과 노하우를 가맹점주님들과 나눕니다.</p>
      </Reveal>
      <Reveal className={styles.rentalFrame}>
        <div
          ref={stageRef}
          className={classNames(styles.rentalStage, isDragging && styles.rentalStageDragging)}
          onPointerDown={(event) => {
            if (event.pointerType === 'mouse' && event.button !== 0) return
            drag.current = { active: true, startX: event.clientX, startY: event.clientY, pointerId: event.pointerId, axis: null }
            event.currentTarget.setPointerCapture(event.pointerId)
          }}
          onPointerMove={(event) => {
            if (drag.current.active) {
              const deltaX = event.clientX - drag.current.startX
              const deltaY = event.clientY - drag.current.startY
              if (!drag.current.axis && Math.max(Math.abs(deltaX), Math.abs(deltaY)) > 4) {
                drag.current.axis = Math.abs(deltaX) > Math.abs(deltaY) ? 'x' : 'y'
              }
              if (drag.current.axis === 'x') {
                setIsDragging(true)
                setDragOffset(deltaX)
              }
            }
          }}
          onPointerUp={finishDrag}
          onPointerCancel={cancelDrag}
        >
          {RENTALS.map((item, index) => {
            const offset = relativeIndex(index)
            const distance = Math.abs(offset)
            const isActive = offset === 0
            const slideGap = offset === 0 ? '' : offset > 0 ? ' + var(--rental-gap)' : ' - var(--rental-gap)'
            const dragProgress = isDragging ? dragOffset / (stageRef.current?.clientWidth || 1) : 0
            const parallaxProgress = Math.max(-1, Math.min(1, offset + dragProgress))
            const parallaxX = -parallaxProgress * 35
            return (
              <article key={item.title} className={classNames(styles.rentalSlide, isActive && styles.rentalSlideActive)} style={{ transform: `translate(-50%, -50%) translateX(calc(${offset * 100}%${slideGap} + ${dragOffset}px))`, zIndex: isActive ? 99 : 3 - distance, transition: isDragging ? 'none' : undefined }}>
                <picture className={styles.rentalPicture} style={{ transform: `translate3d(${parallaxX}%, 0, 0)`, transition: isDragging ? 'none' : undefined }} aria-hidden="true">
                  <source media="(max-width: 800px)" srcSet={item.mobileImage} />
                  <img className={styles.rentalImage} src={item.image} alt="" />
                </picture>
                <span className={styles.rentalShade} aria-hidden="true" />
                <div className={styles.rentalCopy}>
                  <h3 className={classNames(styles.rentalTitle, isActive && styles.rentalTextActive)}>{item.title}</h3>
                  <p className={classNames(styles.rentalDescription, isActive && styles.rentalTextActive)}>{item.description}</p>
                  <a className={classNames(styles.rentalPrice, isActive && styles.rentalTextActive)} href="#inquiry">{item.price}</a>
                </div>
              </article>
            )
          })}
        </div>
        <div className={styles.carouselControls}>
          <div className={styles.carouselDots}>
            {RENTALS.map((item, index) => <button key={item.title} type="button" className={classNames(styles.carouselDot, active === index && styles.carouselDotActive)} aria-label={`${item.title} 보기`} aria-current={active === index ? 'true' : undefined} onClick={() => { setDragOffset(0); setActive(index) }}><span className={styles.carouselProgress} aria-hidden="true"><i /></span></button>)}
          </div>
          <button type="button" className={classNames(styles.carouselArrow, styles.carouselArrowPrevious)} aria-label="이전 렌탈 서비스" disabled={active === 0} onClick={() => move(-1)}>‹</button>
          <button type="button" className={classNames(styles.carouselArrow, styles.carouselArrowNext)} aria-label="다음 렌탈 서비스" disabled={active === RENTALS.length - 1} onClick={() => move(1)}>›</button>
        </div>
      </Reveal>
    </section>
  )
}

function ReasonSection() {
  return (
    <section id="reason" className={classNames(styles.section, styles.reasonSection)}>
      <Reveal className={styles.sectionHead}>
        <h2 className={styles.sectionTitle}><span className={styles.sectionTitleAccent}>"우마이식당"</span>을 선택해야 하는 이유!</h2>
        <p className={styles.sectionDescription}>"우마이식당"만의 경쟁력을 확인하세요.<br />빠른 회전율부터 상생을 위한 운영까지 함께 준비합니다.</p>
      </Reveal>
      <div className={styles.reasonGrid}>
        {REASONS.map((reason, index) => (
          <Reveal key={reason.title} rootMargin="0px 0px 420px 0px" onPointerMove={setFillPosition} className={classNames(styles.reasonCard, index % 2 === 1 && styles.reasonCardRight, styles.revealDelay[index as 0 | 1 | 2 | 3])}>
            <span className={styles.reasonCardFill} aria-hidden="true" />
            <div className={styles.reasonCardText}>
              <h3 className={styles.reasonCardTitle}>{reason.title}</h3>
              <p className={styles.reasonCardDescription}>{reason.description}</p>
            </div>
            <span className={styles.reasonIcon} aria-hidden="true">
              <img className={styles.reasonIconImage} src={reason.image} alt="" />
            </span>
          </Reveal>
        ))}
      </div>
    </section>
  )
}

function BusinessSection() {
  return (
    <section id="business" className={classNames(styles.section, styles.businessSection)}>
      <Reveal className={styles.sectionHead}>
        <h2 className={styles.sectionTitle}>안양 최장수 정통 일본 라멘 전문점<br /><span className={styles.sectionTitleAccent}>우마이식당의 가맹 이야기</span></h2>
        <p className={styles.sectionDescription}>성공과 성장을 이끈 저력과 섬세함으로<br />함께하시는 가맹점 점주님들의 평생의 동반자가 되겠습니다.</p>
      </Reveal>
      <div className={styles.businessGrid}>
        {BUSINESSES.map((business, index) => (
          <Reveal key={business.title} rootMargin="0px 0px 420px 0px" onPointerMove={setFillPosition} className={classNames(styles.businessCard, styles.revealDelay[index as 0 | 1 | 2 | 3])}>
            <span className={styles.businessCardFill} aria-hidden="true" />
            <div className={styles.businessImage} style={{ background: `linear-gradient(145deg, ${business.tone[0]}, ${business.tone[1]})` }} aria-hidden="true">
              <span className={styles.businessImageMark}>{business.eyebrow}</span>
            </div>
            <div className={styles.businessCopy}>
              <h3 className={styles.businessTitle}>{business.title}</h3>
              <p className={styles.businessDescription}>{business.description}</p>
            </div>
            <a className={styles.businessButton} href="#inquiry" onPointerMove={setFillPosition}>
              <span className={styles.businessButtonFill} aria-hidden="true" />
              <span className={styles.businessButtonText}>{business.action}</span>
            </a>
          </Reveal>
        ))}
      </div>
    </section>
  )
}

const ALL_CATEGORY = '전체'

function ReferenceSection() {
  const navigate = useNavigate()
  const { locale = 'kor' } = useParams<{ locale: string }>()
  const { lang } = useAppStore()
  const [activeTab, setActiveTab] = useState(0)

  const allPosts = getAllPosts(lang)
  const tabs = [ALL_CATEGORY, ...getAllCategories(lang)]
  const activeCategory = tabs[activeTab] ?? ALL_CATEGORY
  const posts = (activeCategory === ALL_CATEGORY
    ? allPosts
    : allPosts.filter((post) => post.category === activeCategory)
  ).slice(0, 8)

  const goPost = (slug: string) => navigate(`/${locale}/blog/${slug}`)

  return (
    <section id="reference" className={classNames(styles.section, styles.referenceSection)}>
      <Reveal className={styles.sectionHead}>
        <h2 className={styles.sectionTitle}>라멘 한 그릇에 담은<br /><span className={styles.sectionTitleAccent}>우마이식당의 이야기</span></h2>
        <p className={styles.sectionDescription}>라멘의 역사와 재료, 조리 이야기까지<br />우마이식당 블로그에서 먼저 만나보세요.</p>
      </Reveal>
      {tabs.length > 1 && (
        <div className={styles.referenceTabs} role="tablist" aria-label="블로그 분류">
          {tabs.map((tab, index) => <button key={tab} type="button" role="tab" aria-selected={activeTab === index} className={classNames(styles.referenceTab, activeTab === index && styles.referenceTabActive)} onClick={() => setActiveTab(index)}>{tab}</button>)}
        </div>
      )}
      <div className={styles.referenceGrid}>
        {posts.map((post) => (
          <Reveal key={`${activeCategory}-${post.slug}`} className={styles.referenceCard} onClick={() => goPost(post.slug)} style={{ cursor: 'pointer' }}>
            <img className={styles.referenceImage} src={post.thumbnail} alt={post.title} loading="lazy" />
            <p className={styles.referenceName}>{post.title}</p>
            <p className={styles.referenceDetail}>{post.description}</p>
          </Reveal>
        ))}
      </div>
      <a className={styles.referenceMore} href={`/${locale}/blog`} onClick={(event) => { event.preventDefault(); navigate(`/${locale}/blog`) }}><span className={styles.referenceMoreLabel}>블로그 전체 보기</span></a>
    </section>
  )
}

function MetricCounter({ value, unit }: { value: number; unit: string }) {
  const [count, setCount] = useState(0)
  const ref = useRef<HTMLSpanElement>(null)

  useEffect(() => {
    const node = ref.current
    if (!node) return
    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return
      const duration = 1300
      const start = performance.now()
      const tick = (now: number) => {
        const progress = Math.min((now - start) / duration, 1)
        setCount(Math.floor(value * (1 - Math.pow(1 - progress, 3))))
        if (progress < 1) requestAnimationFrame(tick)
      }
      requestAnimationFrame(tick)
      observer.disconnect()
    }, { threshold: 0.45 })
    observer.observe(node)
    return () => observer.disconnect()
  }, [value])

  return <span ref={ref}>{count.toLocaleString('ko-KR')}<small className={styles.metricCardNumberUnit}>{unit}</small></span>
}

function MetricsSection() {
  const sectionRef = useRef<HTMLElement>(null)
  const [progress, setProgress] = useState(0)
  const [viewport, setViewport] = useState({ width: 1280, height: 720, contentWidth: 1265 })
  const [pinPhase, setPinPhase] = useState<'before' | 'pinned' | 'after'>('before')

  useEffect(() => {
    let frame = 0
    const updateProgress = () => {
      const section = sectionRef.current
      if (!section) return
      const contentWidth = document.documentElement.clientWidth || window.innerWidth
      const nextViewport = { width: window.innerWidth, height: window.innerHeight, contentWidth }
      const sectionTop = section.offsetTop
      const isMobile = nextViewport.width <= 800
      const motionStart = sectionTop - nextViewport.height
      const next = isMobile
        ? Math.max(0, Math.min(1, (window.scrollY - sectionTop + nextViewport.height * 0.2) / Math.max(section.offsetHeight, 1)))
        : Math.max(0, Math.min(1, (window.scrollY - motionStart) / 3000))
      const nextPhase = isMobile ? 'before' : window.scrollY < sectionTop ? 'before' : window.scrollY <= sectionTop + 2200 ? 'pinned' : 'after'
      setProgress(next)
      setViewport(nextViewport)
      setPinPhase(nextPhase)
    }

    const requestUpdate = () => {
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(updateProgress)
    }

    updateProgress()
    window.addEventListener('scroll', requestUpdate, { passive: true })
    window.addEventListener('resize', requestUpdate)
    return () => {
      cancelAnimationFrame(frame)
      window.removeEventListener('scroll', requestUpdate)
      window.removeEventListener('resize', requestUpdate)
    }
  }, [])

  const clamp = (value: number) => Math.max(0, Math.min(1, value))
  const lerp = (from: number, to: number, amount: number) => from + (to - from) * amount
  const between = (start: number, end: number, value: number) => clamp((value - start) / (end - start))
  const viewportWidth = viewport.width
  const rootSize = originRootSize(viewport.contentWidth)
  const metricCardWidth = 44 * rootSize
  const cardSpan = metricCardWidth + 40
  const getCenter = (value: number) => value < .25
    ? lerp(-3, 0, between(0, .25, value))
    : value < .42
      ? 0
      : value < .62
        ? lerp(0, 1, between(.42, .62, value))
        : value < .78
          ? 1
          : lerp(1, 2, between(.78, 1, value))
  const center = getCenter(progress)
  const cardLayout = METRICS.map((_, index) => {
    const distance = index - center
    return { x: distance * cardSpan, y: Math.abs(distance) * 110 }
  })
  const map01Opacity = clamp(1 - Math.abs(0 - center) * 2)
  const map02Opacity = clamp(1 - Math.abs(1 - center) * 2)
  const map03Opacity = clamp(1 - Math.abs(2 - center) * 2)
  const circleOpacity = between(0, .25, progress)
  const circleRotation = lerp(90, 0, circleOpacity)
  const cardHeight = viewportWidth <= 1280 ? 212 : Math.min(320, Math.max(212, viewport.height * .32))
  const cardBaseTop = viewport.height - (viewport.height / 2 + (viewportWidth <= 1280 ? 19 : 22) * rootSize) - cardHeight / 2
  const stagePosition = viewportWidth <= 800
    ? { position: 'relative' as const, top: 0 }
    : pinPhase === 'pinned'
      ? { position: 'fixed' as const, top: 0, left: 0, width: `${viewport.contentWidth}px` }
      : { position: 'absolute' as const, top: pinPhase === 'after' ? '2200px' : 0, left: 0, width: '100%' }

  return (
    <section ref={sectionRef} className={styles.metricsSection} aria-label="우마이식당의 기준">
      <div className={styles.metricsStage} style={stagePosition}>
      <div className={styles.metricCards}>
        {METRICS.map((metric, index) => (
          <article
            key={metric.title}
            className={classNames(styles.metricCard, index === 1 && styles.metricCardAccent, index === 2 && styles.metricCardLight)}
            style={{
              background: `linear-gradient(145deg, ${metric.tone[0]}, ${metric.tone[1]})`,
              transform: `translate3d(calc(-50% + ${cardLayout[index].x}px), ${cardBaseTop + cardLayout[index].y}px, 0)`,
              opacity: clamp(progress * 3),
            }}
          >
            <p className={styles.metricCardTitle}>{metric.title}</p>
            <p className={styles.metricCardNumber}><MetricCounter value={metric.value} unit={metric.unit} /></p>
            <p className={styles.metricCardDescription}>{metric.description}</p>
            <p className={styles.metricCardDetail}>{metric.detail}</p>
          </article>
        ))}
      </div>
      <span className={styles.metricsOrb} style={{ opacity: circleOpacity, transform: `translateX(-50%) rotate(${circleRotation}deg)` }} aria-hidden="true" />
      <div className={styles.metricCenter} aria-hidden="true">
        <div className={styles.metricCenterSeal} style={{ opacity: Math.max(map01Opacity, map02Opacity, map03Opacity) }}>
          <img className={styles.metricCenterImage} src="/images/samples/metrics1.webp" alt="" style={{ opacity: map01Opacity }} />
          <img className={styles.metricCenterImage} src="/images/samples/metrics2.webp" alt="" style={{ opacity: map02Opacity }} />
          <img className={styles.metricCenterImage} src="/images/samples/metrics3.webp" alt="" style={{ opacity: map03Opacity }} />
        </div>
      </div>
      </div>
    </section>
  )
}

function InquirySection() {
  const mapHref = `https://map.naver.com/p/search/${encodeURIComponent(BUSINESS.address)}`

  return (
    <section id="inquiry" className={styles.inquiry}>
      <div className={styles.inquiryPanel}>
        <div className={styles.inquiryInner}>
          <h2 className={styles.inquiryTitle}>우마이식당과 함께<br /><span className={styles.heroAccent}>성공과 성장</span>을 시작하세요</h2>
          <p className={styles.inquiryDescription}>우마이식당의 모든 궁금증을 해결해드리겠습니다.<br />창업 상담 문의를 남겨주시면 바로 연락드리겠습니다.</p>
        </div>
        <div className={styles.inquiryCards}>
          <a className={classNames(styles.inquiryCard, styles.inquiryCardPrimary)} href={BUSINESS.instagram} target="_blank" rel="noreferrer"><span className={styles.inquiryCardTitle}>창업 상담 채널</span><strong className={styles.inquiryCardValue}>Instagram</strong></a>
          {/* <a className={styles.inquiryCard} href={mapHref} target="_blank" rel="noreferrer"><span className={styles.inquiryCardTitle}>안양 최장수 브랜드</span><strong className={styles.inquiryCardValue}>우마이식당</strong></a> */}
          {/* <a className={styles.inquiryCard} href="#top"><span className={styles.inquiryCardTitle}>영업시간</span><strong className={styles.inquiryCardValue}>20:30 라스트오더</strong></a> */}
        </div>
      </div>
    </section>
  )
}

function FloatingInstagram() {
  return (
    <a className={styles.floatingInstagram} href={BUSINESS.instagram} target="_blank" rel="noreferrer" aria-label="우마이식당 Instagram 열기">
      <img className={styles.floatingInstagramImage} src="/images/samples/instagram.webp" alt="" />
    </a>
  )
}

function ReasonBusinessSections() {
  return (
    <div className={styles.reasonBusinessWrap}>
      <div className={styles.reasonBusinessBg} aria-hidden="true" />
      <ReasonSection />
      <BusinessSection />
    </div>
  )
}

export function HomePage() {
  const { locale = 'kor' } = useParams<{ locale: string }>()

  useSeo({
    title: '우마이식당 | 범계 일본라멘·덮밥 전문점',
    description: '범계역에 위치한 안양지역 최장수 일본라멘·덮밥 전문점 우마이식당 범계 본점. 본점의 기준과 가맹사업의 방향을 소개합니다.',
    path: `/${locale}`,
  })

  return (
      <div className={styles.home}>
      <Header />
      <InitialPopup />
      <main>
        <HeroSection />
        <SolutionsSection />
        <RentalSection />
        {/* <ReasonBusinessSections /> */}
        <ReferenceSection />
        <MetricsSection />
        <InquirySection />
      </main>
      <Footer />
      <FloatingInstagram />
    </div>
  )
}
